import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Notice, Tag } from '../../../common/components/detail/detail';
import { Gate } from '../../../global/contexts/auth/auth';
import { useRequestId } from '../../../core/network/idempotency';
import { WORKFLOW_STATE_LABEL } from '../../tasks/utils/form';
import { PROPOSALS_PAGE_SIZE, useAuthorizeTasks, useProposals } from '../contexts/breakdown';
import AuthorizeConfirm from './authorize-confirm';

import '../styles/breakdown.css';

// The tier's task proposals, and the selection a person authorizes.
//
// A TASK WRITTEN BY AN AGENT IS STILL A PROPOSAL. Every task is born
// `proposed`, the queue does not hand it out, and only a human session with
// `tasks:authorize` moves it to `ready`. That is the sentence this screen has
// to keep saying, because a well-written proposal looks exactly like work that
// was decided.

const STATE_TONE = { proposed: 'neutral', ready: 'active', discarded: 'danger' };

export default function Proposals({ tierId, tier, breakdown }) {
    const [offset, setOffset] = useState(0);
    const { data, isLoading, error } = useProposals(tierId, { offset });

    // The selection is HELD BY ID, never by row position. A list that reorders
    // or repaginates between clicking and sending would otherwise authorize a
    // different task than the one somebody ticked — and nothing about the
    // screen would look wrong.
    const [selected, setSelected] = useState(() => new Set());
    const [confirming, setConfirming] = useState(false);
    const [rowErrors, setRowErrors] = useState(null);

    const authorize = useAuthorizeTasks(tierId);
    const keyFor = useRequestId();

    const items = useMemo(() => data?.items ?? [], [data]);
    const total = data?.pagination?.total ?? items.length;
    const agreed = tier?.breakdown_state === 'agreed' || tier?.breakdown_state === 'legacy';
    const revision = tier?.breakdown_revision ?? breakdown?.breakdown_revision;

    const selectable = items.filter(item => item.workflow_state === 'proposed');
    const pageAllSelected = selectable.length > 0
        && selectable.every(item => selected.has(item.id));

    const toggle = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
        setRowErrors(null);
    };

    const togglePage = () => {
        setSelected(prev => {
            const next = new Set(prev);
            if (pageAllSelected) selectable.forEach(item => next.delete(item.id));
            else selectable.forEach(item => next.add(item.id));
            return next;
        });
        setRowErrors(null);
    };

    // The rows being authorized, with the spec_revision each one was READ with.
    // If a proposal changed in between, the backend refuses that row and the
    // whole batch fails — which is correct: the signature covers a specific
    // text, not a title.
    const chosen = useMemo(
        () => items.filter(item => selected.has(item.id)),
        [items, selected],
    );

    // Selected ids that are not on this page. They still count and still get
    // sent, so the screen has to say they exist rather than show a number that
    // does not match the ticks.
    const offPage = selected.size - chosen.length;

    const handleAuthorize = async () => {
        const tasks = chosen.map(item => ({
            task_id: item.id,
            spec_revision: item.spec_revision,
        }));

        const body = {
            request_id: keyFor({ tier: tierId, revision, tasks }),
            breakdown_revision: revision,
            tasks,
        };

        try {
            const result = await authorize.mutateAsync(body);
            setConfirming(false);
            setSelected(new Set());
            setRowErrors(null);

            const done = (result?.tasks ?? tasks).length;
            toast.success(
                result?.request?.replayed
                    ? `Ya estaban autorizadas: se reconcilió el resultado de ${done}.`
                    : `${done} tarea${done === 1 ? '' : 's'} autorizada${done === 1 ? '' : 's'}`,
            );
        } catch (err) {
            setConfirming(false);
            // THE SELECTION SURVIVES. Losing it on failure means redoing the
            // reading somebody just did, and this is the screen where the
            // reading is the work.
            const errors = err.detail?.errors;
            if (Array.isArray(errors)) {
                const byTask = new Map();
                errors.forEach(row => {
                    const id = row?.task_id ?? chosen[row?.index]?.id;
                    if (id != null) byTask.set(id, row?.message ?? row?.field ?? 'No se pudo autorizar');
                });
                setRowErrors(byTask);
            }

            if (err.isRevisionConflict) {
                toast.error('Algo cambió mientras elegías. No se autorizó ninguna: releé y volvé a elegir.');
                return;
            }
            if (err.isIdempotencyConflict) {
                toast.error('Esa misma operación ya se envió con otro contenido. Revisá la selección.');
                return;
            }
            if (err.isUnconfirmed) {
                toast.error('No pudimos confirmar el resultado. Reintentá con la misma selección: lleva la misma clave y no se autoriza dos veces.');
                return;
            }
            toast.error(err.message || 'No se autorizó ninguna tarea');
        }
    };

    if (isLoading) return <p className="breakdown__loading">Cargando propuestas...</p>;

    if (error) {
        return <p className="breakdown__failure">{error.message || 'No se pudieron cargar las propuestas'}</p>;
    }

    return (
        <section className="breakdown__card">
            <header className="breakdown__card-head">
                <h3 className="breakdown__card-title">Propuestas de tareas</h3>
                <div className="breakdown__tags">
                    <Tag>{total} en total</Tag>
                </div>
            </header>

            {!agreed && (
                <Notice tone="warn">
                    El tier todavía no está acordado. <strong>No se pueden proponer tareas</strong>
                    {' '}hasta que su alcance esté acordado — el backend las rechaza.
                </Notice>
            )}

            {items.length === 0 ? (
                <Notice>
                    Este tier todavía no tiene propuestas. Las crea un agente maestro por MCP, o
                    una persona con <code>tasks:write</code> en lote.
                    {' '}<strong>El panel no genera tareas</strong>: no habla con ningún proveedor
                    de IA, y prometer un botón que las escriba sería prometer algo que no existe.
                </Notice>
            ) : (
                <>
                    <div className="breakdown__selectbar">
                        <label className="breakdown__checkline">
                            <input
                                type="checkbox"
                                checked={pageAllSelected}
                                onChange={togglePage}
                                disabled={selectable.length === 0}
                            />
                            {/* Said explicitly, because "select all" over a
                                paginated list is the classic place a person
                                thinks they selected 120 and selected 25. */}
                            <span>
                                Seleccionar las {selectable.length} propuestas <strong>de esta
                                página</strong>
                                {total > items.length && ` (hay ${total} en total)`}
                            </span>
                        </label>

                        {selected.size > 0 && (
                            <Gate domain="tasks:authorize">
                                <button
                                    type="button"
                                    className="breakdown__button breakdown__button--primary"
                                    onClick={() => setConfirming(true)}
                                    disabled={authorize.isPending || !agreed}
                                >
                                    Autorizar {selected.size} tarea{selected.size === 1 ? '' : 's'}
                                </button>
                            </Gate>
                        )}
                    </div>

                    {offPage > 0 && (
                        <p className="breakdown__note">
                            {offPage} de las seleccionadas están en otra página. Se envían igual.
                        </p>
                    )}

                    <ul className="breakdown__proposals">
                        {items.map(item => (
                            <li key={item.id} className="breakdown__proposal">
                                <label className="breakdown__proposal-pick">
                                    <input
                                        type="checkbox"
                                        checked={selected.has(item.id)}
                                        onChange={() => toggle(item.id)}
                                        disabled={item.workflow_state !== 'proposed'}
                                        aria-label={`Seleccionar ${item.title}`}
                                    />
                                </label>

                                <div className="breakdown__proposal-body">
                                    <Link to={`/admin/tasks/${item.id}`} className="breakdown__proposal-title">
                                        {item.title}
                                    </Link>

                                    <div className="breakdown__tags">
                                        <Tag tone={STATE_TONE[item.workflow_state] ?? 'neutral'}>
                                            {WORKFLOW_STATE_LABEL[item.workflow_state] ?? item.workflow_state}
                                        </Tag>
                                        {item.kind && <Tag>{item.kind}</Tag>}
                                        {item.estimate && <Tag>{item.estimate}</Tag>}
                                        {item.repository?.name && <Tag>{item.repository.name}</Tag>}
                                        {item.spec_revision != null && <Tag>rev. {item.spec_revision}</Tag>}
                                    </div>

                                    {/* The two things that decide whether it can
                                        be authorized, visible without opening it. */}
                                    {item.open_questions?.length > 0 && (
                                        <p className="breakdown__proposal-flag">
                                            {item.open_questions.length} duda
                                            {item.open_questions.length === 1 ? '' : 's'} abierta
                                            {item.open_questions.length === 1 ? '' : 's'} — no se
                                            puede autorizar así.
                                        </p>
                                    )}
                                    {!item.acceptance_criteria?.length && (
                                        <p className="breakdown__proposal-flag">
                                            Sin criterios de aceptación — no se puede autorizar así.
                                        </p>
                                    )}

                                    {rowErrors?.has(item.id) && (
                                        <p className="breakdown__proposal-error">
                                            {rowErrors.get(item.id)}
                                        </p>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>

                    {total > PROPOSALS_PAGE_SIZE && (
                        <div className="breakdown__actions">
                            <button
                                type="button"
                                className="breakdown__button"
                                onClick={() => setOffset(value => Math.max(0, value - PROPOSALS_PAGE_SIZE))}
                                disabled={offset === 0}
                            >
                                Anterior
                            </button>
                            <span className="breakdown__note">
                                {offset + 1}–{Math.min(offset + items.length, total)} de {total}
                            </span>
                            <button
                                type="button"
                                className="breakdown__button"
                                onClick={() => setOffset(value => value + PROPOSALS_PAGE_SIZE)}
                                disabled={offset + items.length >= total}
                            >
                                Siguiente
                            </button>
                        </div>
                    )}
                </>
            )}

            {confirming && (
                <AuthorizeConfirm
                    tasks={chosen}
                    offPage={offPage}
                    revision={revision}
                    busy={authorize.isPending}
                    onCancel={() => setConfirming(false)}
                    onConfirm={handleAuthorize}
                />
            )}
        </section>
    );
}
